import React from 'react'
import { Button, Dialog, IconButton, Input, Switch, Table, type Column } from '@/components/ds'
import { Ic } from '@/components/icons'
import { QueryState, Val } from '@/components/common'
import { listUnsubscribes } from '@/lib/db'
import { addUnsubscribe, getSendConfig, removeUnsubscribe } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { useToast } from '@/lib/toast'
import { useTheme } from '@/lib/theme'
import { useAuth } from '@/lib/auth'
import { CHART } from '@/lib/dashboard'
import { initials, isValidEmail, shortDateTime } from '@/lib/format'
import type { Unsubscribe } from '@shared/types'
import type { ScreenProps } from '@/App'

/**
 * Settings, on the same glass the dashboard uses.
 *
 * Every surface here is `.lm-card`, which paints with --surface-card /
 * --glass-edge — so light, dark and the backdrop blobs are all inherited rather
 * than restated. The one deliberate exception is the terminal chip, which stays
 * dark in both themes because a light "terminal" stops reading as one.
 */
export function SettingsScreen({ narrow }: ScreenProps) {
  const toast = useToast()
  const { theme, setTheme } = useTheme()
  const { email, signOut } = useAuth()

  const config = useAsync(() => getSendConfig(), [])
  const suppression = useAsync(() => listUnsubscribes(), [])

  const [newEmail, setNewEmail] = React.useState('')
  const [confirmRemove, setConfirmRemove] = React.useState<Unsubscribe | null>(null)
  const [busy, setBusy] = React.useState(false)

  const live = config.data?.send_enabled === true

  async function add() {
    if (!isValidEmail(newEmail)) {
      toast('error', 'That email looks wrong')
      return
    }
    setBusy(true)
    try {
      await addUnsubscribe(newEmail.trim(), 'added manually')
      toast('success', 'Added to the suppression list', 'Nothing will send to it again')
      setNewEmail('')
      suppression.reload()
    } catch (e) {
      toast('error', 'Could not add', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function remove(row: Unsubscribe) {
    setBusy(true)
    try {
      await removeUnsubscribe(row.email)
      toast('info', 'Removed from the suppression list')
      setConfirmRemove(null)
      suppression.reload()
    } catch (e) {
      toast('error', 'Could not remove', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const columns: Column<Unsubscribe>[] = [
    { key: 'email', label: 'Email', render: (r) => <Val muted={false}>{r.email}</Val> },
    {
      key: 'reason',
      label: 'Reason',
      render: (r) => (
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.reason ?? '—'}</span>
      ),
    },
    {
      key: 'at',
      label: 'When',
      align: 'right',
      nowrap: true,
      render: (r) => (
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{shortDateTime(r.at)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (r) => (
        <IconButton label="Remove from list" size="sm" onClick={() => setConfirmRemove(r)}>
          <Ic.trash size={15} />
        </IconButton>
      ),
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 940 }}>
      {/* --- send engine ---------------------------------------------------- */}
      <section className="lm-card">
        <div className="lm-card-head">
          <span
            className="lm-ico"
            style={{ background: `linear-gradient(140deg, ${CHART.indigo}, #7d7bea)` }}
          >
            <Ic.send size={15} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 className="lm-card-title">Send engine</h2>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: narrow ? 'minmax(0,1fr)' : 'minmax(0, 1fr) minmax(0, 1.1fr)',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', minWidth: 0 }}>
            <span
              className="lm-dial"
              style={{ color: live ? CHART.green : CHART.amber }}
              aria-hidden="true"
            >
              {live ? 'LIVE' : 'DRY'}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
                {live ? 'Delivering real mail' : 'Dry run — nothing is delivered'}
              </div>
              <p className="lm-card-sub">
                {live
                  ? 'The Worker fires every minute for anything whose scheduled time has passed.'
                  : 'The Worker does every step except the SMTP handoff, and writes a “would_send” line to the log instead.'}
              </p>
            </div>
          </div>

          <pre className="lm-term">
            <span className="c"># flip the engine on</span>
            {'\n'}
            <span className="n">1 </span>add a real SMTP mailbox on <b>Identities</b>
            {'\n'}
            <span className="n">2 </span>set <span className="s">SEND_ENABLED:&quot;true&quot;</span>{' '}
            <span className="c">(var, not secret)</span>
            {'\n'}
            <span className="n">3 </span>npx wrangler deploy --config worker/wrangler.jsonc
          </pre>
        </div>
      </section>

      {/* --- base URL + appearance ------------------------------------------ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? 'minmax(0,1fr)' : 'repeat(2, minmax(0, 1fr))',
          gap: 14,
        }}
      >
        <section className="lm-card">
          <div className="lm-card-head">
            <span
              className="lm-ico"
              style={{ background: `linear-gradient(140deg, ${CHART.blue}, #6aa8ff)` }}
            >
              <Ic.link size={15} />
            </span>
            <h2 className="lm-card-title">Public base URL</h2>
          </div>
          <p className="lm-card-sub" style={{ margin: '0 0 4px' }}>
            Where your unsubscribe &amp; tracking links point.
          </p>
          <div className="lm-kv">
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>URL</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Used in every email footer
              </div>
            </div>
            <span className="lm-kv-val">{config.data?.public_base_url ?? '—'}</span>
          </div>
        </section>

        <section className="lm-card">
          <div className="lm-card-head">
            <span
              className="lm-ico"
              style={{ background: `linear-gradient(140deg, ${CHART.amber}, #ffd166)` }}
            >
              <Ic.sun size={15} />
            </span>
            <h2 className="lm-card-title">Appearance</h2>
          </div>
          <p className="lm-card-sub" style={{ margin: '0 0 4px' }}>
            Light by default — your choice is remembered on this device.
          </p>
          <div className="lm-kv">
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
              Dark theme
            </div>
            <Switch checked={theme === 'dark'} onChange={(v) => setTheme(v ? 'dark' : 'light')} />
          </div>
        </section>
      </div>

      {/* --- suppression list ------------------------------------------------ */}
      <section className="lm-card">
        <div className="lm-card-head">
          <span
            className="lm-ico"
            style={{ background: `linear-gradient(140deg, ${CHART.red}, #ff7a7a)` }}
          >
            <Ic.ban size={15} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="lm-card-title">Suppression list</h2>
          </div>
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: 'var(--accent-text)',
              background: 'var(--accent-soft)',
              border: '1px solid var(--accent-soft-border)',
              borderRadius: 999,
              padding: '3px 10px',
              whiteSpace: 'nowrap',
            }}
          >
            Checked before every send
          </span>
        </div>

        <p className="lm-card-sub" style={{ margin: '0 0 12px' }}>
          One-click unsubscribes from real emails land here automatically. You can also add an
          address by hand.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'stretch',
            flexDirection: narrow ? 'column' : 'row',
            marginBottom: 14,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <Input
              type="email"
              value={newEmail}
              onChange={setNewEmail}
              placeholder="someone@example.com"
            />
          </div>
          <Button disabled={busy || !newEmail} onClick={add} icon={<Ic.ban size={14} />}>
            Suppress
          </Button>
        </div>

        <QueryState
          loading={suppression.loading}
          error={suppression.error}
          empty={(suppression.data ?? []).length === 0}
          emptyTitle="Nobody suppressed"
          emptyDescription="Addresses that unsubscribe show up here."
          onRetry={suppression.reload}
        >
          <div className="lm-table-frame lm-solid">
            <div className="scroll-x">
              <Table
                rowKey="id"
                columns={columns}
                rows={suppression.data ?? []}
                stackBelow={narrow ? 9999 : 560}
              />
            </div>
          </div>
        </QueryState>
      </section>

      {/* --- how it works ---------------------------------------------------- */}
      <section className="lm-card">
        <div className="lm-card-head">
          <span
            className="lm-ico"
            style={{ background: `linear-gradient(140deg, ${CHART.green}, #4fd0a0)` }}
          >
            <Ic.template size={15} />
          </span>
          <h2 className="lm-card-title">How sending works</h2>
        </div>
        <ol
          style={{
            margin: 0,
            paddingLeft: 18,
            display: 'flex',
            flexDirection: 'column',
            gap: 7,
            fontSize: 12.5,
            lineHeight: 1.55,
            color: 'var(--text-secondary)',
          }}
        >
          <li>
            <b>Add a sender</b> on Identities — that mailbox authenticates and is always the From
            address.
          </li>
          <li>
            <b>Write a template.</b> Use Insert&nbsp;field for merge tokens: the top group comes
            from your identity and campaign, the bottom group from each lead.
          </li>
          <li>
            <b>Build a campaign</b> in the wizard: details, leads, template, then Review. It refuses
            to launch while a sender token like <code>{'{{company}}'}</code> is still blank.
          </li>
          <li>
            <b>Delay off</b> sends as soon as you finish; <b>delay on</b> spaces leads out with a
            five-minute floor. Either way the Worker picks rows up on its own each minute.
          </li>
          <li>
            <b>Watch the Queue</b> for what is about to go, and <b>Send logs</b> for what happened.
            A campaign reads <b>Done</b> once every lead has been processed.
          </li>
        </ol>
      </section>

      {/* --- account -------------------------------------------------------- */}
      <section className="lm-card">
        <div className="lm-card-head">
          <span
            className="lm-ico"
            style={{ background: `linear-gradient(140deg, ${CHART.indigo}, ${CHART.pink})` }}
          >
            <Ic.shield size={15} />
          </span>
          <h2 className="lm-card-title">Account</h2>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 38,
              height: 38,
              flex: 'none',
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              fontWeight: 650,
              color: '#fff',
              background: `linear-gradient(140deg, ${CHART.indigo}, ${CHART.pink})`,
            }}
          >
            {initials(email)}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13.5,
                fontWeight: 500,
                color: 'var(--text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {email ?? '—'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Administrator</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            icon={<Ic.logout size={14} />}
            onClick={() => void signOut()}
          >
            Sign out
          </Button>
        </div>
      </section>

      <Dialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        title="Remove from the suppression list?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmRemove(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => confirmRemove && remove(confirmRemove)}
            >
              Remove
            </Button>
          </>
        }
      >
        <Val muted={false}>{confirmRemove?.email}</Val> becomes mailable again. Only do this if they
        asked to be re-subscribed — sending to someone who opted out is how domains get blocklisted.
      </Dialog>
    </div>
  )
}
